/**
 * Modal Registry
 *
 * Central registry for all modals in the application.
 * Type-safe with explicit lazy imports for proper Vite resolution.
 *
 * To add a new modal:
 * 1. Create your modal component with default export
 * 2. Add the name to ModalName enum in types.ts
 * 3. Add import line here: [ModalName.YOUR_MODAL]: () => import("path").then(m => ({ default: m.default }))
 */

import { lazy, ComponentType } from "react";
import { ModalName, type BaseModalProps } from "./types";

// Null modal for legacy/unused modals
const nullModal = () => Promise.resolve({ default: () => null });

/**
 * Modal Registry - Single source of truth for all modals
 */
export const modalRegistry: Record<
  ModalName,
  () => Promise<{ default: ComponentType<any> }>
> = {
  // Shared modals (in src/shared/modals/components/)
  [ModalName.CONFIRM]: () =>
    import("@/shared/widgets/Confirm").then((m) => ({ default: m.default })),
  [ModalName.SCORES_EDIT]: () =>
    import("@/shared/modals/components/ScoresEdit").then((m) => ({
      default: m.default,
    })),
  [ModalName.DIMENSION_DETAILS]: () =>
    import("@/shared/modals/components/DimensionDetails").then((m) => ({
      default: m.default,
    })),
  [ModalName.NEW_ACCOUNT]: () =>
    import("@/shared/modals/components/NewAccount").then((m) => ({
      default: m.default,
    })),
  [ModalName.IMAGE_CROPPR_UPLOADER]: () =>
    import("@/shared/modals/components/ImageCropperUploader").then((m) => ({
      default: m.default,
    })),
  [ModalName.ATTACHMENT_UPLOAD]: () =>
    import("@/shared/modals/components/AttachmentUpload").then((m) => ({
      default: m.default,
    })),
  [ModalName.MEMBER]: () =>
    import("@/shared/modals/components/Member").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Teams
  [ModalName.NEW_TEAM]: () =>
    import("@/features/teams/modals/NewTeam").then((m) => ({
      default: m.default,
    })),
  [ModalName.MODIFY_TEAM]: () =>
    import("@/features/teams/modals/ModifyTeam").then((m) => ({
      default: m.default,
    })),
  [ModalName.TEAM_PICKER]: () =>
    import("@/features/teams/modals/TeamPicker").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Projects
  [ModalName.NEW_PROJECT]: () =>
    import("@/features/projects/modals/NewProject").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Scorecards
  [ModalName.NEW_SCORECARD]: () =>
    import("@/features/scorecards/modals/NewScorecard").then((m) => ({
      default: m.default,
    })),
  [ModalName.SCORECARD]: () =>
    import("@/features/scorecards/modals/Scorecard").then((m) => ({
      default: m.default,
    })),
  [ModalName.CLONE_SCORECARD]: () =>
    import("@/features/scorecards/modals/CloneScorecard").then((m) => ({
      default: m.default,
    })),
  [ModalName.SCORECARD_PICKER]: () =>
    import("@/features/scorecards/modals/ScorecardPicker").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Organization
  [ModalName.NEW_ORGANIZATION]: () =>
    import("@/features/organization/modals/NewOrganization").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Profile
  [ModalName.CHANGE_PASSWORD]: () =>
    import("@/features/profile/modals/ChangePassword").then((m) => ({
      default: m.default,
    })),

  // Feature modals - People
  [ModalName.ADD_PEOPLE]: () =>
    import("@/features/people/modals/AddPeople").then((m) => ({
      default: m.default,
    })),
  // Feature modals - People
  [ModalName.ADD_PEOPLE_V2]: () =>
    import("@/features/people/modals/AddPeopleV2").then((m) => ({
      default: m.default,
    })),

  // Feature modals - Assessments
  [ModalName.ASSESSMENT_ROW_DETAILS]: () =>
    import("@/features/assessments/modals/AssessmentRowDetails").then((m) => ({
      default: m.default,
    })),

  [ModalName.INFO]: () =>
    import("@/shared/modals/components/Info").then((m) => ({
      default: m.default,
    })),

  [ModalName.ASSESSMENT_REPORT_GENERATE]: () =>
    import("@/features/assessments/modals/AssessmentReportGenerate").then(
      (m) => ({
        default: m.default,
      })
    ),

  // Legacy/Unused modals - return null to prevent errors
  [ModalName.VIDEO_MODAL]: () =>
    import("@/shared/modals/components/VideoModal").then((m) => ({
      default: m.default,
    })),
  [ModalName.INVITE_REVIEWERS]: nullModal,
  [ModalName.INVITE_PEOPLE]: nullModal,
  [ModalName.LEARN_MORE]: nullModal,
  [ModalName.KEY_OBJECTIVES_EDIT]: nullModal,
  [ModalName.REQUIREMENTS_EDIT]: nullModal,
  [ModalName.INVITE]: () =>
    import("@/shared/modals/components/Invite").then((m) => ({
      default: m.default,
    })),
};

/**
 * Get a lazy-loaded modal component by name
 */
export function getModal(modalName: ModalName): ComponentType<BaseModalProps> {
  const modalLoader = modalRegistry[modalName];

  if (!modalLoader) {
    const availableModals = Object.keys(modalRegistry).join(", ");
    throw new Error(
      `Modal "${modalName}" not found in registry. Available modals: ${availableModals}`
    );
  }

  return lazy(modalLoader as any);
}

/**
 * Check if a modal exists in the registry
 */
export function hasModal(modalName: string): modalName is ModalName {
  return modalName in modalRegistry;
}

/**
 * Get all available modal names
 */
export function getAvailableModals(): ModalName[] {
  return Object.keys(modalRegistry) as ModalName[];
}
